import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './EmployeeEmergencyContact.css';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { positionRank, supervisorsAscending, roleBadge } from '../lib/positionRank';

function formatTel(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'number' && Number.isFinite(v)) return String(v).replace(/\.0$/, '');
  const s = String(v).trim();
  if (/^\d+\.0$/.test(s)) return s.slice(0, -2);
  return s;
}

function normalizePayload(data) {
  if (Array.isArray(data)) return { people: data };
  return { people: Array.isArray(data?.people) ? data.people : [] };
}

// 連絡順・並び順・バッジはすべて共通のランク方式で判定する（組織図と統一）。
// これにより課長・部長がいなくても、マネージャー・取締役員等が正しく上位として扱われる。

/**
 * 連絡順（エスカレーション）：同じ課の上司を「下位 → 上位」の順に並べる。
 * 例: リーダー → 主任/係長 → 課長代理 → 課長/マネージャー → 部長 → 取締役
 * 課長・部長が居なくても、マネージャー等がそのまま連絡先になる。
 */
function buildEscalationChain(people) {
  return supervisorsAscending(people).map(person => ({
    key: person.memberId || person.id || person.name,
    label: (person.position || '').trim() || '上司',
    person,
  }));
}

export default function EmployeeEmergencyContact() {
  const [people, setPeople] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [escStepIndex, setEscStepIndex] = useState(0);
  const [escAttempts, setEscAttempts] = useState(0);
  const navigate = useNavigate();

  const fetchEmployees = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/employees/in-my-group/');
      const { people: list } = normalizePayload(data);
      setPeople((list || []).map(e => ({ ...e, tel: formatTel(e.tel), email: (e.email || '').trim() })));
    } catch (err) {
      console.error('緊急連絡先取得エラー:', err);
      if (err.response?.status === 401) {
        setError('ログインの有効期限が切れました。再度ログインしてください。');
      } else {
        setError('緊急連絡先の読み込みに失敗しました。ネットワーク接続を確認してください。');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const sortedPeople = useMemo(
    () =>
      [...people].sort((a, b) => {
        // 1) 役職ランクが高い人ほど上（マネージャー等も正しく上位に並ぶ）
        // 2) 同ランク（一般社員など）はコード(memberId)の昇順
        const ra = positionRank(a.position);
        const rb = positionRank(b.position);
        if (ra !== rb) return rb - ra;
        return String(a.memberId || '').localeCompare(String(b.memberId || ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }),
    [people]
  );

  const escalationChain = useMemo(() => buildEscalationChain(sortedPeople), [sortedPeople]);
  const maxEscIndex = Math.max(0, escalationChain.length - 1);
  const safeEscIndex = Math.min(escStepIndex, maxEscIndex);
  const currentEsc = escalationChain[safeEscIndex];

  useEffect(() => {
    setEscStepIndex(0);
    setEscAttempts(0);
  }, [people]);

  useEffect(() => {
    if (escAttempts < 3) return;
    if (safeEscIndex >= maxEscIndex) return;
    setEscStepIndex(i => Math.min(i + 1, maxEscIndex));
    setEscAttempts(0);
  }, [escAttempts, safeEscIndex, maxEscIndex]);

  const handleBack = () => navigate('/card_back');
  //const handleBack = () => navigate('/card_front');
  const bumpEscalationAttempt = () => setEscAttempts(a => a + 1);
  const dial = tel => {
    if (!tel) return;
    window.location.href = `tel:${tel}`;
  };
  const sendMail = email => {
    if (!email) return;
    window.location.href = `mailto:${email}`;
  };

  if (isLoading) {
    return (
      <div className="eec-container">
        <h2 className="eec-title">緊急連絡先</h2>
        <div className="eec-loading">読み込み中…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="eec-container">
        <h2 className="eec-title">緊急連絡先</h2>
        <div className="eec-error">
          <p>{error}</p>
          <button type="button" onClick={fetchEmployees} className="eec-btn-retry">
            再試行
          </button>
        </div>
        <div className="eec-footer">
          <button type="button" onClick={handleBack} className="eec-btn-back">
            戻る
          </button>
        </div>
      </div>
    );
  }

  if (people.length === 0) {
    return (
      <div className="eec-container">
        <h2 className="eec-title">緊急連絡先</h2>
        <p className="eec-empty">緊急連絡先の情報がありません。</p>
        <div className="eec-footer">
          <button type="button" onClick={handleBack} className="eec-btn-back">
            戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="eec-container">
      <h2 className="eec-title">緊急連絡先</h2>
      <div className="eec-bcp-integration">
        <Link to="/bcp" className="eec-btn-bcp">
          <span className="eec-bcp-icon">📢</span>
          <div className="eec-bcp-text">
            <strong>BCP 避難所検索</strong>
            <p>避難場所</p>
          </div>
        </Link>
      </div>

      {escalationChain.length > 0 && (
        <div className="eec-escalation">
          <div className="eec-escalation-title">緊急時の連絡の目安</div>
          <p className="eec-escalation-note">
            まずは<strong>リーダー</strong>へ連絡し、<strong>3回</strong>つながらない場合は<strong>課長代理</strong>、次に<strong>課長</strong>へ連絡してください。
          </p>
          <div className="eec-escalation-steps">
            {escalationChain.map((step, idx) => {
              const unlocked = idx <= safeEscIndex;
              const isCurrent = idx === safeEscIndex;
              const p = step.person;
              return (
                <div
                  key={step.key}
                  className={[
                    'eec-esc-step',
                    unlocked ? 'eec-esc-step-unlocked' : 'eec-esc-step-locked',
                    isCurrent ? 'eec-esc-step-current' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className="eec-esc-step-head">
                    <span className="eec-esc-step-label">{step.label}</span>
                    {!unlocked && <span className="eec-esc-step-hint">（前段3回後に表示）</span>}
                  </div>
                  {unlocked && p && (
                    <div className="eec-esc-step-body">
                      <span className="eec-esc-step-name">{step.label}：{p.name}</span>
                      <div className="eec-esc-step-actions">
                        <button
                          type="button"
                          className="eec-btn-icon"
                          title="電話をかける"
                          disabled={!p.tel}
                          onClick={() => dial(p.tel)}
                        >
                          📞
                        </button>
                        <button
                          type="button"
                          className="eec-btn-icon eec-btn-mail"
                          title="メールを送信"
                          disabled={!p.email}
                          onClick={() => sendMail(p.email)}
                        >
                          ✉
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {currentEsc && (
            <div className="eec-escalation-action">
              <button type="button" className="eec-btn-try" onClick={bumpEscalationAttempt}>
                「{currentEsc.label}」へ連絡を試みた（{escAttempts} / 3 回）
              </button>
            </div>
          )}
        </div>
      )}

      <div className="eec-group">
        <div className="eec-employee-list">
          {sortedPeople.map(emp => {
            const rb = roleBadge(emp.position);
            return (
              <div key={emp.memberId || `${emp.name}-${emp.position}`} className="eec-card">
                <div className="eec-card-top">
                  <span className={`eec-badge eec-badge--${rb.slug}`}>{rb.text}</span>
                  <div className="eec-card-name">
                    <strong>{emp.name || '—'}</strong>
                  </div>
                  <div className="eec-card-icons">
                    <button
                      type="button"
                      className="eec-btn-icon"
                      title="電話をかける"
                      disabled={!emp.tel}
                      onClick={() => dial(emp.tel)}
                    >
                      📞
                    </button>
                    <button
                      type="button"
                      className="eec-btn-icon eec-btn-mail"
                      title="メールを送信"
                      disabled={!emp.email}
                      onClick={() => sendMail(emp.email)}
                    >
                      ✉
                    </button>
                  </div>
                </div>
                {emp.position && <div className="eec-card-row">役職: {emp.position}</div>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="eec-footer">
        <button type="button" onClick={handleBack} className="eec-btn-back">
          戻る
        </button>
      </div>
    </div>
  );
}
